import { useEffect, useCallback, useRef, useState } from "react";

/**
 * Stripped of react-table entirely.
 * - productsList is a flat array kept in a ref for virtualizer access
 *   and also in state (only updated via surgical patches) for re-renders.
 * - reconciliedData mutations go through stable callbacks so columns
 *   never need to be recreated.
 */
const useReconcileStockEditView = ({
  products,
  setReconciliedData,
  reconciliedData,
  isReviewMode = false,
  isDraft = true,
}) => {
  // Normalize row in draft: if system_count is null, fallback to current_system_count
  const normalizeItem = useCallback(
    (p) => {
      if (
        isDraft &&
        (p?.system_count === null || p?.system_count === undefined)
      ) {
        return {
          ...p,
          system_count: p?.current_system_count ?? p?.system_count,
        };
      }
      return p;
    },
    [isDraft],
  );

  // ── productsList ──────────────────────────────────────────────────────────
  // We keep a ref for zero-cost reads inside callbacks
  // and a state copy so the virtualizer triggers re-renders only for
  // the single patched row.
  const [productsList, setProductsList] = useState(() =>
    products.map(normalizeItem),
  );
  const productsListRef = useRef(productsList);

  // Sync ref whenever state changes
  useEffect(() => {
    productsListRef.current = productsList;
  }, [productsList]);

  // Incremental append (infinite scroll pages arrive one-by-one)
  const prevLenRef = useRef(products.length);
  useEffect(() => {
    if (products.length === prevLenRef.current) return;
    if (products.length < prevLenRef.current) {
      prevLenRef.current = products.length;
      setProductsList(products.map(normalizeItem));
      return;
    }
    const newItems = products.slice(prevLenRef.current);
    prevLenRef.current = products.length;
    setProductsList((prev) => {
      const next = [...prev];
      newItems.forEach((p) => {
        // honour any already-reconciled data for this item
        const item = reconciliedData?.[p?.item_id] ?? p;
        next.push(normalizeItem(item));
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length, normalizeItem]);

  // Refetch (refresh button): same rows, fresh server values.
  // - system_count (displayed) <- current_system_count ?? system_count
  // - physical_count (displayed) <- current_physical_count ?? physical_count,
  //   but only for rows without unsaved edits so typed values are preserved.
  const isFirstProductsRef = useRef(true);
  useEffect(() => {
    if (isFirstProductsRef.current) {
      isFirstProductsRef.current = false;
      return;
    }
    if (isReviewMode) return;

    const firstDefined = (...values) => values.find((v) => v !== undefined);
    const buildPatch = (fresh, { includePhysical, includeFlag, prevRow }) => {
      const patch = {};
      ["current_system_count", "current_physical_count"].forEach((key) => {
        if (fresh?.[key] !== undefined) patch[key] = fresh[key];
      });
      const system = firstDefined(
        fresh?.current_system_count,
        fresh?.system_count,
      );
      if (system !== undefined) patch.system_count = system;

      let physical;
      if (includePhysical) {
        physical = firstDefined(
          fresh?.current_physical_count,
          fresh?.physical_count,
        );
        if (physical !== undefined) patch.physical_count = physical;
      }

      if (includeFlag) {
        const effectivePhysical =
          physical !== undefined ? physical : prevRow?.physical_count;
        const hasPhysical =
          effectivePhysical !== null &&
          effectivePhysical !== undefined &&
          effectivePhysical !== "";
        const effectivePrevSystem =
          prevRow?.system_count !== null && prevRow?.system_count !== undefined
            ? prevRow?.system_count
            : prevRow?.current_system_count;
        const systemChanged =
          system !== undefined &&
          effectivePrevSystem !== undefined &&
          Number(effectivePrevSystem) !== Number(system);
        if (
          (systemChanged && hasPhysical) ||
          fresh?.system_count_changed === true ||
          prevRow?.system_count_changed === true
        ) {
          patch.system_count_changed = true;
        }
      } else if (prevRow?.system_count_changed === false || !isDraft) {
        patch.system_count_changed = false;
      }

      return patch;
    };
    const hasDiff = (row, patch) =>
      Object.keys(patch).some((key) => row?.[key] !== patch[key]);
    const freshById = new Map(products.map((p) => [p?.item_id, p]));
    const editedIds = new Set(
      Object.values(reconciliedRef.current || {}).map((item) => item?.item_id),
    );

    setProductsList((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const fresh = freshById.get(row?.item_id);
        if (!fresh) return row;
        const isEdited = editedIds.has(row?.item_id);
        const patch = buildPatch(fresh, {
          includePhysical: !isEdited,
          includeFlag: isDraft && !isEdited,
          prevRow: row,
        });
        if (!hasDiff(row, patch)) return row;
        changed = true;
        return { ...row, ...patch };
      });
      return changed ? next : prev;
    });
    setReconciliedData((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        const fresh = freshById.get(next[key]?.item_id);
        if (!fresh) return;
        const patch = buildPatch(fresh, {
          includePhysical: false,
          includeFlag: false,
          prevRow: next[key],
        });
        if (!hasDiff(next[key], patch)) return;
        changed = true;
        next[key] = { ...next[key], ...patch };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // ── reconciliedData callbacks ──────────────────────────────────────────────
  const reconciliedRef = useRef(reconciliedData);
  useEffect(() => {
    reconciliedRef.current = reconciliedData;
  }, [reconciliedData]);

  const updateChanges = useCallback(
    (id, newData) => {
      setReconciliedData((prev) => ({ ...prev, [id]: newData }));
    },
    [setReconciliedData],
  );

  const removeReconciledKey = useCallback(
    (id) => {
      setReconciliedData((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    },
    [setReconciliedData],
  );

  // Patch a single row without touching the rest of the list
  const updateRow = useCallback((rowIndex, patch) => {
    setProductsList((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], ...patch };
      return next;
    });
  }, []);

  return {
    productsList,
    productsListRef,
    reconciliedRef,
    updateChanges,
    removeReconciledKey,
    updateRow,
  };
};

export default useReconcileStockEditView;
